import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident, getCurrentYear} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {NatResidentParkingUsesWithdrawalLocationState} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import {NatPutParkingUsesWithdrawalRequest} from '../../repository/NatPutParkingUsesWithdrawal'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import PPSelectForResident, {Option} from '../../../../../components/shared/forms/resident/PPSelectForResident'
import {MONTH} from '../../../../../CodeMaster'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentParkingUsesWithdrawalLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    carCarryingOutDate: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutParkingUsesWithdrawalRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    withdrawalArea: string
    withdrawalDateYear: string
    withdrawalDateMonth: string
    carCarryingOutDate: string

    currentDate: moment.Moment
    holidays: string[]
    yearOptions: Option[]
    monthOptions: Option[]
    clearWithdrawalDateError: boolean

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentParkingUsesWithdrawalForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            withdrawalArea: '',
            withdrawalDateYear: '',
            withdrawalDateMonth: '',
            carCarryingOutDate: '',
            currentDate: moment(),
            holidays: [],
            yearOptions: [],
            monthOptions: [],
            clearWithdrawalDateError: false,
            isLoadCompleted: false,
        }

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeWithdrawalDateYear = this.handleChangeWithdrawalDateYear.bind(this)
        this.handleChangeWithdrawalDateMonth = this.handleChangeWithdrawalDateMonth.bind(this)
        this.handleChangeCarCarryingOutDate = this.handleChangeCarCarryingOutDate.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            // 現在日時取得
            if (currentTimeResponse && currentTimeResponse.details) {
                const currentYear: number = getCurrentYear(`${currentTimeResponse.details.currentDateTime}`)
                const yearOptions: Option[] = [{label: '', value: ''}]
                const monthOptions: Option[] = [{label: '', value: ''}]

                let year: number = currentYear + 3
                for (let index: number = 1; index <= 7; index++) {
                    yearOptions.push({label: `${year}`, value: `${year}`})
                    year--
                }

                MONTH.values().map((month) => {
                    return (
                        monthOptions.push({
                            label: `${month.label}`, value: (String(month.value).length === 1)
                                ? '0' + String(month.value) : String(month.value),
                        })
                    )
                })
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                    yearOptions: yearOptions,
                    monthOptions: monthOptions,
                })
            }
            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const withdrawalArea = this.props.withdrawalArea ?? this.state.withdrawalArea
            const withdrawalDate = this.props.withdrawalDate ? this.props.withdrawalDate.split('-') : []
            const carCarryingOutDate = this.props.carCarryingOutDate ?? this.state.carCarryingOutDate

            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                withdrawalArea: withdrawalArea,
                withdrawalDateYear: withdrawalDate[0] ?? this.state.withdrawalDateYear,
                withdrawalDateMonth: withdrawalDate[1] ?? this.state.withdrawalDateMonth,
                carCarryingOutDate: carCarryingOutDate,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeWithdrawalDateYear(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalDateYear: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeWithdrawalDateMonth(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalDateMonth: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeCarCarryingOutDate(date: string): void {
        this.setState({carCarryingOutDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalDateYear, withdrawalDateMonth, carCarryingOutDate,
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !withdrawalArea ||
            !withdrawalDateYear || !withdrawalDateMonth || !carCarryingOutDate) {
            applyButtonDisabled = true
        }
        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalDateYear, withdrawalDateMonth, carCarryingOutDate,
        } = this.state

        const withdrawalDateYearMonth = withdrawalDateYear + '-' + withdrawalDateMonth

        let request: Nullable<NatPutParkingUsesWithdrawalRequest>

        request = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            withdrawalArea: withdrawalArea,
            withdrawalDate: withdrawalDateYearMonth,
            carCarryingOutDate: carCarryingOutDate,
            confirmed: false,
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWithdrawalAreaForm(): ReactElement {
        const {withdrawalArea} = this.state

        return (
            <>
                <ItemHeading>
                    解約する区画
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'駐車場の区画番号'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        maxLength: 20,
                                        name: 'withdrawalArea',
                                        value: withdrawalArea,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['withdrawalArea']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWithdrawalDateForm(): ReactElement {
        const {withdrawalDateYear, withdrawalDateMonth} = this.state
        return (
            <>
                <ItemHeading>
                    解約日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>駐車場の契約を<BreakPcOnly/>解約する日付</span>}
                        required
                        component={
                            <ErrorMessageAreaWrapper>
                                <DisplayFlex>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalDateYear,
                                            }}
                                            placeholder={'年'}
                                            onChange={this.handleChangeWithdrawalDateYear}
                                            options={this.state.yearOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalDateMonth,
                                            }}
                                            placeholder={'月'}
                                            onChange={this.handleChangeWithdrawalDateMonth}
                                            options={this.state.monthOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            messageInvisible
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={8}/>
                                    </FlexContainer>
                                    <StringFlexDiv>末日</StringFlexDiv>
                                </DisplayFlex>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderCarCarryingOutDateForm(): ReactElement {
        const {carCarryingOutDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    自動車搬出日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>駐車場から自動車を<BreakPcOnly/>搬出する日付</span>}
                        required
                        component={
                            <PPDatePickerForResident
                                date={carCarryingOutDate}
                                onChange={this.handleChangeCarCarryingOutDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['carCarryingOutDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'10px'}/>
                </WideWhiteArea>
            </>
        )
    }


    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderWithdrawalAreaForm()}
                < Box height={'15px'}/>
                {this.renderWithdrawalDateForm()}
                <Box height={'15px'}/>
                {this.renderCarCarryingOutDateForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}


const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`

const UnitBoxArea: StyledDiv = styled.div`
  width: 150px;
  ${currentMedia.greaterThan('pc')`
    width: 165px;
  `};
`

const DisplayFlex: StyledDiv = styled.div`
  display: flex;
`

const StringFlexDiv: StyledDiv = styled.div`
  display: flex;
  max-height: 50px;
  align-items: flex-end;
`
